import { useState, useEffect, useCallback } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import api, { formatError, formatDoctorName } from "@/utils/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { Plus, Search, FlaskConical, ChevronRight, Edit, Upload, Paperclip, Download, X as XIcon, FileText, Image as ImageIcon } from "lucide-react";

const TEST_TYPES = ["blood", "urine", "thyroid", "liver", "kidney", "lipid", "hba1c", "xray", "mri", "ct_scan", "usg", "ecg", "other"];
const STATUS_FLOW = {
  pending: { next: "in_progress", label: "Start", color: "bg-blue-600 hover:bg-blue-700" },
  in_progress: { next: "completed", label: "Complete", color: "bg-emerald-600 hover:bg-emerald-700" },
  completed: { next: null },
};
const STATUS_STYLES = {
  pending: "bg-amber-100 text-amber-800",
  in_progress: "bg-blue-100 text-blue-800",
  completed: "bg-emerald-100 text-emerald-800",
};

export default function Lab() {
  const { t } = useLanguage();
  const { toast } = useToast();
  const [tests, setTests] = useState([]);
  const [patients, setPatients] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [showResult, setShowResult] = useState(false);
  const [selectedTest, setSelectedTest] = useState(null);
  const [form, setForm] = useState({ patient_id: "", doctor_id: "", test_name: "", test_type: "blood", notes: "", charge: "" });
  const [result, setResult] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploadTest, setUploadTest] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [previewAtt, setPreviewAtt] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const params = filterStatus ? `?status=${filterStatus}` : "";
      const { data } = await api.get(`/lab/tests${params}`);
      setTests(data);
    } catch (err) {
      toast({ title: "Error", description: formatError(err), variant: "destructive" });
    } finally { setLoading(false); }
  }, [filterStatus, toast]);

  useEffect(() => {
    fetchData();
    Promise.all([api.get("/patients"), api.get("/doctors")])
      .then(([p, d]) => {
        setPatients(p.data || []);
        setDoctors(d.data || []);
      })
      .catch((err) => {
        console.error("Failed to load patients/doctors metadata:", err);
      });
  }, [fetchData]);

  const updateStatus = async (test, nextStatus) => {
    try {
      await api.put(`/lab/tests/${test.id}`, { status: nextStatus });
      toast({ title: `Status: ${nextStatus}` });
      fetchData();
    } catch (err) {
      toast({ title: "Error", description: formatError(err), variant: "destructive" });
    }
  };

  const saveResult = async () => {
    try {
      await api.put(`/lab/tests/${selectedTest.id}`, { result, status: "completed" });
      toast({ title: "Result saved" });
      setShowResult(false);
      fetchData();
    } catch (err) {
      toast({ title: "Error", description: formatError(err), variant: "destructive" });
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      await api.post("/lab/tests", { ...form, charge: parseFloat(form.charge) || 0 });
      toast({ title: "Lab test created" });
      setShowModal(false);
      fetchData();
    } catch (err) {
      toast({ title: "Error", description: formatError(err), variant: "destructive" });
    } finally { setSaving(false); }
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length || !uploadTest) return;
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        await api.post(`/lab/tests/${uploadTest.id}/attachments`, fd, {
          headers: { "Content-Type": "multipart/form-data" }
        });
      }
      toast({ title: `${files.length} file${files.length > 1 ? "s" : ""} uploaded` });
      // Refresh
      const { data } = await api.get(`/lab/tests?status=${filterStatus}`);
      setTests(data);
      const refreshed = data.find(t => t.id === uploadTest.id);
      if (refreshed) setUploadTest(refreshed);
      e.target.value = "";
    } catch (err) {
      toast({ title: "Upload failed", description: formatError(err), variant: "destructive" });
    } finally { setUploading(false); }
  };

  const handleDeleteAttachment = async (storedName) => {
    if (!uploadTest || !window.confirm("Remove this attachment?")) return;
    try {
      await api.delete(`/lab/tests/${uploadTest.id}/attachments/${storedName}`);
      toast({ title: "Attachment removed" });
      const { data } = await api.get(`/lab/tests?status=${filterStatus}`);
      setTests(data);
      const refreshed = data.find(t => t.id === uploadTest.id);
      if (refreshed) setUploadTest(refreshed);
    } catch (err) {
      toast({ title: "Error", description: formatError(err), variant: "destructive" });
    }
  };

  const downloadAttachment = async (att) => {
    try {
      const r = await api.get(att.download_url.replace("/api", ""), { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = att.original_name; a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast({ title: "Download failed", description: formatError(err), variant: "destructive" });
    }
  };

  const filtered = tests.filter(t =>
    t.test_name?.toLowerCase().includes(search.toLowerCase()) ||
    t.patient_name?.toLowerCase().includes(search.toLowerCase())
  );

  const counts = {
    pending: tests.filter(t => t.status === "pending").length,
    in_progress: tests.filter(t => t.status === "in_progress").length,
    completed: tests.filter(t => t.status === "completed").length,
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-sans">{t("lab")}</h1>
          <p className="text-slate-500 text-sm mt-1">Manage lab tests and radiology</p>
        </div>
        <Button className="bg-emerald-700 hover:bg-emerald-800 text-white gap-2" onClick={() => setShowModal(true)} data-testid="add-lab-btn">
          <Plus size={16} /> Add Test
        </Button>
      </div>

      {/* Status Counts */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: "Pending", key: "pending", color: "text-amber-600", bg: "bg-amber-50" },
          { label: "In Progress", key: "in_progress", color: "text-blue-600", bg: "bg-blue-50" },
          { label: "Completed", key: "completed", color: "text-emerald-600", bg: "bg-emerald-50" },
        ].map(({ label, key, color, bg }) => (
          <button key={key} onClick={() => setFilterStatus(filterStatus === key ? "" : key)} className={`${bg} rounded-xl p-4 text-left cursor-pointer border-2 transition-colors ${filterStatus === key ? "border-current" : "border-transparent"}`} data-testid={`lab-filter-${key}`}>
            <FlaskConical size={18} className={`${color} mb-2`} />
            <p className={`text-2xl font-bold font-sans ${color}`}>{counts[key]}</p>
            <p className="text-xs text-slate-600">{label}</p>
          </button>
        ))}
      </div>

      {/* Search & Filter */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 flex gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input placeholder="Search tests..." className="pl-9 border-slate-200" value={search} onChange={e => setSearch(e.target.value)} data-testid="lab-search" />
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead className="font-semibold text-slate-700">Patient</TableHead>
              <TableHead className="font-semibold text-slate-700">Test</TableHead>
              <TableHead className="font-semibold text-slate-700">Type</TableHead>
              <TableHead className="font-semibold text-slate-700">Charge</TableHead>
              <TableHead className="font-semibold text-slate-700">Status</TableHead>
              <TableHead className="font-semibold text-slate-700">{t("actions")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={6} className="text-center py-12 text-slate-400">{t("loading")}</TableCell></TableRow>
            ) : filtered.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center py-12 text-slate-400">
                <FlaskConical size={32} className="mx-auto mb-2 opacity-30" /><p>No lab tests found</p>
              </TableCell></TableRow>
            ) : filtered.map(test => (
              <TableRow key={test.id} className="table-row-hover" data-testid={`lab-row-${test.id}`}>
                <TableCell>
                  <p className="font-medium text-slate-900 text-sm">{test.patient_name}</p>
                </TableCell>
                <TableCell>
                  <p className="text-sm font-medium text-slate-800">{test.test_name}</p>
                  {test.notes && <p className="text-xs text-slate-500">{test.notes}</p>}
                </TableCell>
                <TableCell>
                  <Badge variant="outline" className="capitalize text-xs">{test.test_type?.replace("_", " ")}</Badge>
                </TableCell>
                <TableCell className="text-sm text-slate-700">{test.charge ? `₹${test.charge}` : "—"}</TableCell>
                <TableCell>
                  <Badge className={`text-xs ${STATUS_STYLES[test.status] || "bg-slate-100 text-slate-700"}`}>
                    {test.status?.replace("_", " ")}
                  </Badge>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-1">
                    {STATUS_FLOW[test.status]?.next && (
                      <Button size="sm" className={`h-7 text-xs text-white ${STATUS_FLOW[test.status].color}`} onClick={() => updateStatus(test, STATUS_FLOW[test.status].next)} data-testid={`lab-status-btn-${test.id}`}>
                        {STATUS_FLOW[test.status].label}
                      </Button>
                    )}
                    {test.status !== "completed" && (
                      <Button variant="ghost" size="sm" className="h-7 text-xs text-slate-500 hover:text-slate-900 gap-1" onClick={() => { setSelectedTest(test); setResult(test.result || ""); setShowResult(true); }} data-testid={`add-result-btn-${test.id}`}>
                        <Edit size={12} /> Result
                      </Button>
                    )}
                    {test.status === "completed" && test.result && (
                      <span className="text-xs text-emerald-700 font-medium truncate max-w-24">{test.result}</span>
                    )}
                    <Button variant="ghost" size="sm" className={`h-7 w-7 p-0 ${test.attachments?.length ? "text-emerald-600" : "text-slate-400 hover:text-slate-900"}`} onClick={() => setUploadTest(test)} data-testid={`attach-btn-${test.id}`} title={`${test.attachments?.length || 0} attachment${test.attachments?.length !== 1 ? "s" : ""}`}>
                      <Paperclip size={14} />
                      {test.attachments?.length > 0 && <span className="text-[10px] font-bold ml-0.5">{test.attachments.length}</span>}
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Add Test Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-sans">Add Lab Test</DialogTitle></DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4 mt-2">
            <div>
              <Label className="text-sm text-slate-700">Patient</Label>
              <select value={form.patient_id} onChange={e => setForm({...form, patient_id: e.target.value})} className="mt-1 w-full border border-slate-200 rounded-md px-3 py-2 text-sm bg-white" required data-testid="lab-patient-select">
                <option value="">Select Patient</option>
                {patients.map(p => <option key={p.id} value={p.id}>{p.name} ({p.patient_id})</option>)}
              </select>
            </div>
            <div>
              <Label className="text-sm text-slate-700">Referring Doctor</Label>
              <select value={form.doctor_id} onChange={e => setForm({...form, doctor_id: e.target.value})} className="mt-1 w-full border border-slate-200 rounded-md px-3 py-2 text-sm bg-white">
                <option value="">Select Doctor (optional)</option>
                {doctors.map(d => <option key={d.id} value={d.id}>{formatDoctorName(d.name)}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm text-slate-700">Test Name</Label>
                <Input value={form.test_name} onChange={e => setForm({...form, test_name: e.target.value})} placeholder="e.g. CBC, Blood Sugar" className="mt-1 border-slate-200" required data-testid="lab-test-name" />
              </div>
              <div>
                <Label className="text-sm text-slate-700">Test Type</Label>
                <select value={form.test_type} onChange={e => setForm({...form, test_type: e.target.value})} className="mt-1 w-full border border-slate-200 rounded-md px-3 py-2 text-sm bg-white capitalize" data-testid="lab-test-type">
                  {TEST_TYPES.map(t => <option key={t} value={t}>{t.replace("_", " ")}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-sm text-slate-700">Charge (₹)</Label>
                <Input type="number" value={form.charge} onChange={e => setForm({...form, charge: e.target.value})} className="mt-1 border-slate-200" />
              </div>
              <div>
                <Label className="text-sm text-slate-700">Notes</Label>
                <Input value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} placeholder="Optional" className="mt-1 border-slate-200" />
              </div>
            </div>
            <div className="flex justify-end gap-3">
              <Button type="button" variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
              <Button type="submit" className="bg-emerald-700 hover:bg-emerald-800 text-white" disabled={saving} data-testid="save-lab-btn">
                {saving ? "Saving..." : "Create Test"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Result Modal */}
      <Dialog open={showResult} onOpenChange={setShowResult}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-sans">Add Result — {selectedTest?.test_name}</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <p className="text-sm text-slate-600">Patient: <span className="font-medium">{selectedTest?.patient_name}</span></p>
            <div>
              <Label className="text-sm text-slate-700">Test Result</Label>
              <textarea value={result} onChange={e => setResult(e.target.value)} placeholder="Enter test result..." rows={4} className="mt-1 w-full border border-slate-200 rounded-md px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-emerald-500/50" data-testid="lab-result-input" />
            </div>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setShowResult(false)}>Cancel</Button>
              <Button className="bg-emerald-700 hover:bg-emerald-800 text-white" onClick={saveResult} data-testid="save-result-btn">Save Result</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Attachments Modal */}
      <Dialog open={!!uploadTest} onOpenChange={() => setUploadTest(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-sans flex items-center gap-2">
              <Paperclip size={16} className="text-emerald-600" /> Lab Report Files
            </DialogTitle>
          </DialogHeader>
          {uploadTest && (
            <div className="space-y-4 mt-2">
              <div className="bg-slate-50 rounded-lg p-3 text-sm">
                <p className="font-semibold text-slate-900">{uploadTest.test_name}</p>
                <p className="text-xs text-slate-500">Patient: {uploadTest.patient_name}</p>
              </div>
              <div>
                <label className="block">
                  <div className="border-2 border-dashed border-slate-200 rounded-xl p-6 text-center hover:border-emerald-400 hover:bg-emerald-50/30 cursor-pointer transition-colors" data-testid="upload-dropzone">
                    <Upload size={24} className="mx-auto mb-2 text-slate-400" />
                    <p className="text-sm font-medium text-slate-700">
                      {uploading ? "Uploading..." : "Click to upload PDF or image"}
                    </p>
                    <p className="text-xs text-slate-500 mt-1">Max 10 MB · PDF, PNG, JPG, WEBP</p>
                  </div>
                  <input type="file" className="hidden" accept="application/pdf,image/*" multiple onChange={handleFileUpload} disabled={uploading} data-testid="upload-file-input" />
                </label>
              </div>

              {uploadTest.attachments?.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Attached Files ({uploadTest.attachments.length})</p>
                  <div className="space-y-2 max-h-80 overflow-y-auto">
                    {uploadTest.attachments.map(att => (
                      <AttachmentRow
                        key={att.stored_name}
                        att={att}
                        onPreview={() => setPreviewAtt(att)}
                        onDownload={() => downloadAttachment(att)}
                        onDelete={() => handleDeleteAttachment(att.stored_name)}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Preview Modal */}
      <Dialog open={!!previewAtt} onOpenChange={() => setPreviewAtt(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="font-sans flex items-center gap-2 truncate">
              {previewAtt?.mime_type?.startsWith("image/") ? <ImageIcon size={16} className="text-violet-600 flex-shrink-0" /> : <FileText size={16} className="text-rose-600 flex-shrink-0" />}
              <span className="truncate">{previewAtt?.original_name}</span>
            </DialogTitle>
          </DialogHeader>
          {previewAtt && <AttachmentPreview att={previewAtt} />}
          <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
            <Button variant="outline" size="sm" onClick={() => previewAtt && downloadAttachment(previewAtt)} className="gap-1.5" data-testid="preview-download-btn">
              <Download size={14} /> Download
            </Button>
            <Button size="sm" onClick={() => setPreviewAtt(null)}>Close</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function AttachmentRow({ att, onPreview, onDownload, onDelete }) {
  const [thumbUrl, setThumbUrl] = useState(null);
  useEffect(() => {
    if (!att.mime_type?.startsWith("image/")) return;
    let revoked = false;
    let currentUrl = null;
    api.get(att.download_url.replace("/api", ""), { responseType: "blob" })
      .then(r => {
        if (revoked) return;
        currentUrl = URL.createObjectURL(r.data);
        setThumbUrl(currentUrl);
      })
      .catch(() => {});
    return () => { revoked = true; if (currentUrl) URL.revokeObjectURL(currentUrl); };
  }, [att.download_url, att.mime_type]);

  const isImage = att.mime_type?.startsWith("image/");
  return (
    <div className="flex items-center gap-3 bg-white border border-slate-200 rounded-lg p-2.5 hover:border-emerald-300 transition-colors" data-testid={`attachment-${att.stored_name}`}>
      <button onClick={onPreview} className="flex-shrink-0" data-testid={`preview-${att.stored_name}`}>
        {isImage && thumbUrl ? (
          <img src={thumbUrl} alt={att.original_name} className="w-12 h-12 rounded-lg object-cover border border-slate-200" />
        ) : (
          <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${isImage ? "bg-violet-50" : "bg-rose-50"}`}>
            {isImage ? <ImageIcon size={18} className="text-violet-600" /> : <FileText size={18} className="text-rose-600" />}
          </div>
        )}
      </button>
      <button onClick={onPreview} className="flex-1 min-w-0 text-left">
        <p className="text-sm font-medium text-slate-900 truncate hover:text-emerald-700">{att.original_name}</p>
        <p className="text-xs text-slate-500">{(att.size / 1024).toFixed(1)} KB · {new Date(att.uploaded_at).toLocaleDateString()}</p>
      </button>
      <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-slate-500 hover:text-emerald-600" onClick={onDownload} data-testid={`download-${att.stored_name}`}>
        <Download size={14} />
      </Button>
      <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-slate-500 hover:text-rose-600" onClick={onDelete} data-testid={`remove-attachment-${att.stored_name}`}>
        <XIcon size={14} />
      </Button>
    </div>
  );
}

function AttachmentPreview({ att }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    let revoked = false;
    let currentUrl = null;
    api.get(att.download_url.replace("/api", ""), { responseType: "blob" })
      .then(r => {
        if (revoked) return;
        currentUrl = URL.createObjectURL(r.data);
        setBlobUrl(currentUrl);
      })
      .catch(e => setErr("Failed to load preview"));
    return () => { revoked = true; if (currentUrl) URL.revokeObjectURL(currentUrl); };
  }, [att.download_url]);

  if (err) return <div className="py-10 text-center text-sm text-rose-600">{err}</div>;
  if (!blobUrl) return <div className="py-10 text-center text-sm text-slate-400">Loading preview...</div>;
  if (att.mime_type?.startsWith("image/")) {
    return (
      <div className="flex-1 overflow-auto bg-slate-50 rounded-lg flex items-center justify-center p-4" data-testid="preview-image">
        <img src={blobUrl} alt={att.original_name} className="max-w-full max-h-[70vh] rounded-md shadow-sm" />
      </div>
    );
  }
  if (att.mime_type === "application/pdf") {
    return (
      <iframe src={blobUrl} title={att.original_name} className="flex-1 w-full border border-slate-200 rounded-lg min-h-[60vh]" data-testid="preview-pdf" />
    );
  }
  return (
    <div className="py-10 text-center text-sm text-slate-500">
      Preview unavailable for this file type. Please download to view.
    </div>
  );
}
